/** Tenant-wide automated OIDC onboarding policy. */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { queryKeys } from "@/lib/query-keys";
import { DynregPage } from "@/features/oauth-dynreg/pages/dynreg-page";
import { fetchIATs, fetchPolicy, fetchRegistrationReviews } from "@/features/oauth-dynreg/queries";
import { canManageAutomatedOnboarding } from "@/features/oauth-dynreg/security-policy";
import { fetchAutomatedOnboardingCapability } from "@/features/product-areas/queries";

const searchSchema = z.object({
  include_revoked: z.boolean().optional(),
});

export const Route = createFileRoute(
  "/_authed/tenants/$slug/settings/automated-onboarding",
)({
  validateSearch: (raw: Record<string, unknown>) => searchSchema.parse(raw),
  beforeLoad: async ({ context, params }) => {
    const permissions = context.authority.permissions;
    const canManage = canManageAutomatedOnboarding(permissions);
    if (!canManage) {
      throw redirect({
        to: "/tenants/$slug/applications",
        params: { slug: params.slug },
        replace: true,
      });
    }
    const capability = await context.queryClient.ensureQueryData({
      queryKey: queryKeys.productAreas.automatedOnboarding(context.tenant.id),
      queryFn: () => fetchAutomatedOnboardingCapability(context.tenant.id),
    });
    if (capability.data.availability !== "hidden") return;
    throw redirect({
      to: "/tenants/$slug/applications",
      params: { slug: params.slug },
      replace: true,
    });
  },
  loaderDeps: ({ search }) => ({ includeRevoked: !!search.include_revoked }),
  loader: async ({ context, deps }) => {
    const tenantID = context.tenant.id;
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: queryKeys.oauthDynreg.iats(tenantID, deps.includeRevoked),
        queryFn: () => fetchIATs(tenantID, deps.includeRevoked),
      }),
      context.queryClient.ensureQueryData({
        queryKey: queryKeys.oauthDynreg.reviews(tenantID, "pending"),
        queryFn: () => fetchRegistrationReviews(tenantID),
      }),
      context.queryClient.ensureQueryData({
        queryKey: queryKeys.oauthDynreg.policy(tenantID),
        queryFn: () => fetchPolicy(tenantID),
      }),
    ]);
  },
  component: DynregPage,
});
